import { useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { getStoredAuth, setStoredAuth } from './api/client'
import { useHotelProfile } from './api/hooks'
import type { Customer } from './api/types'
import { Layout } from './layouts/Layout'
import { HomePage } from './routes/HomePage'
import { RoomsPage } from './routes/RoomsPage'
import { RoomDetailPage } from './routes/RoomDetailPage'
import { AmenitiesPage } from './routes/AmenitiesPage'
import { RestaurantPage } from './routes/RestaurantPage'
import { ComayaguaPage } from './routes/ComayaguaPage'
import { ExplorePage } from './routes/ExplorePage'
import { GalleryPage } from './routes/GalleryPage'
import { AboutPage } from './routes/AboutPage'
import { ContactPage } from './routes/ContactPage'
import { BookingPage } from './routes/BookingPage'
import { ConfirmationPage } from './routes/ConfirmationPage'
import { AccountPage } from './routes/AccountPage'
import { PublicCheckInPage } from './routes/PublicCheckInPage'
import { PublicGuestIntakePage } from './routes/PublicGuestIntakePage'

export default function App() {
  const queryClient = useQueryClient()
  const [auth, setAuth] = useState<{ token: string; customer: Customer } | null>(() =>
    getStoredAuth(),
  )
  const { data: hotel } = useHotelProfile()

  const setCustomerAuth = (next: { token: string; customer: Customer } | null) => {
    setAuth(next)
    setStoredAuth(next)
    queryClient.invalidateQueries({ queryKey: ['customer-reservations'] })
  }

  return (
    <Routes>
      {/* Standalone check-in flow — no marketing chrome (opened from a WhatsApp link). */}
      <Route path="/check-in/:token" element={<PublicCheckInPage hotel={hotel} />} />
      <Route path="/guest-intake/:token" element={<PublicGuestIntakePage hotel={hotel} />} />
      <Route
        path="*"
        element={
          <Layout
            hotel={hotel}
            customer={auth?.customer || null}
            onLogout={() => setCustomerAuth(null)}
          >
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/rooms" element={<RoomsPage />} />
              <Route path="/rooms/:slug" element={<RoomDetailPage />} />
              <Route path="/amenities" element={<AmenitiesPage />} />
              <Route path="/restaurant" element={<RestaurantPage />} />
              <Route path="/comayagua" element={<ComayaguaPage />} />
              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route
                path="/booking"
                element={
                  <BookingPage
                    customer={auth?.customer || null}
                    setCustomerAuth={setCustomerAuth}
                  />
                }
              />
              <Route path="/confirmation" element={<ConfirmationPage />} />
              <Route
                path="/account"
                element={
                  <AccountPage
                    customer={auth?.customer || null}
                    setCustomerAuth={setCustomerAuth}
                  />
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        }
      />
    </Routes>
  )
}
